import { useCallback, useEffect } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import {
  Colors,
  Flex,
  IconChevronDown,
  Typography,
} from '@banhala/ui-kit';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import type { ScreensMetaResponse } from 'shared/src/api';
import { bridge } from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useFilterListAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/useFilterListAtom';
import { useNavigation } from '@react-navigation/native';

type DropdownNavigatorData = NonNullable<
  ScreensMetaResponse['screen']['dropdown_navigator']
>;

type DropdownNavigatorTriggerProps = {
  selectedTitle: string;
  isOpen: boolean;
  onToggle: () => void;
};

export const DropdownNavigatorTrigger = ({
  selectedTitle,
  isOpen,
  onToggle,
}: DropdownNavigatorTriggerProps) => {
  const rotation = useSharedValue(0);

  useEffect(() => {
    rotation.value = withTiming(isOpen ? 180 : 0, { duration: 200 });
  }, [isOpen, rotation]);

  const animatedChevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <Pressable onPress={onToggle}>
      <Flex.Row alignItems="center" gap={2}>
        <Typography type="h5" color="content_primary" numberOfLines={1}>
          {selectedTitle}
        </Typography>
        <Animated.View style={animatedChevronStyle}>
          <IconChevronDown size={16} fill={Colors.content_primary} />
        </Animated.View>
      </Flex.Row>
    </Pressable>
  );
};

type DropdownNavigatorPanelProps = {
  title: string;
  dropdownNavigator: DropdownNavigatorData;
  onClose: () => void;
};

const ITEM_HEIGHT = 44;
const ANIMATION_DURATION = 250;

export const DropdownNavigatorPanel = ({
  title,
  dropdownNavigator,
  onClose,
}: DropdownNavigatorPanelProps) => {
  const { replaceFilterListAtom, filterListAtomValue } = useFilterListAtom();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const { openDeeplink } = useOpenDeeplink();
  const itemCount = dropdownNavigator.navigator_list.length;
  const maxHeight = itemCount * ITEM_HEIGHT;
  const height = useSharedValue(0);
  const dimOpacity = useSharedValue(0);

  useEffect(() => {
    height.value = withTiming(maxHeight, {
      duration: ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
    });
    dimOpacity.value = withTiming(1, { duration: ANIMATION_DURATION });
  }, [height, dimOpacity, maxHeight]);

  const animatedListStyle = useAnimatedStyle(() => ({
    height: height.value,
    overflow: 'hidden' as const,
  }));

  const animatedDimStyle = useAnimatedStyle(() => ({
    opacity: dimOpacity.value,
  }));

  const handleClose = useCallback(() => {
    height.value = withTiming(0, {
      duration: ANIMATION_DURATION,
      easing: Easing.in(Easing.cubic),
    });
    dimOpacity.value = withTiming(0, { duration: ANIMATION_DURATION });
    setTimeout(onClose, ANIMATION_DURATION);
  }, [height, dimOpacity, onClose]);

  const handleSelectItem = useCallback(
    (item: DropdownNavigatorData['navigator_list'][number]) => {
      if (item.navigator_title === dropdownNavigator.selected_title) {
        handleClose();
        return;
      }

      bridge.logAnalyticsEvent({
        name: 'CLICK_DROPDOWN_DETAIL',
        analytics: {
          SCREEN: filterListAtomValue.screen_name,
          CATEGORY_NAME: item.navigator_title,
        },
      });

      if (item.deeplink) {
        handleClose();
        openDeeplink(item.deeplink);
      } else {
        replaceFilterListAtom({ screen_name: item.screen_name });
        navigation.setParams({
          screen_name: item.screen_name,
          next_token: item.next_token,
        });
        handleClose();
      }
    },
    [handleClose, dropdownNavigator.selected_title, replaceFilterListAtom, navigation, filterListAtomValue.screen_name, openDeeplink],
  );

  return (
    <>
      <Animated.View style={animatedListStyle}>
        <Flex
          backgroundColor="bg_always_white"
          borderBottomLeftRadius={12}
          borderBottomRightRadius={12}
          overflow="hidden"
        >
          {dropdownNavigator.navigator_list.map((item, index) => (
            <Pressable
              key={`${item.screen_name}_${index}`}
              testID={`${title}?LIST_INDEX=${index}&VALUE=${item.navigator_title}`}
              onPress={() => handleSelectItem(item)}
            >
              <Flex
                padding={{ vertical: 12 }}
                alignItems="center"
              >
                <Typography
                  type="subtitle1"
                  color={
                    item.navigator_title === dropdownNavigator.selected_title
                      ? 'content_primary'
                      : 'content_tertiary'
                  }
                >
                  {item.navigator_title}
                </Typography>
              </Flex>
            </Pressable>
          ))}
        </Flex>
      </Animated.View>
      <Animated.View style={[styles.dim, animatedDimStyle]}>
        <Pressable style={styles.dimPressable} onPress={handleClose} />
      </Animated.View>
    </>
  );
};

const styles = StyleSheet.create({
  dim: {
    flex: 1,
  },
  dimPressable: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
});
